import { useEffect, useMemo, useRef, useState } from "react";
import {
  addMonths,
  fmtIsoDate,
  fmtMonthHeader,
  getMonthMatrix,
  isWeekend,
  sameDay,
  startOfDay,
  WEEKDAYS_ISO,
} from "../DateSelector/dateUtils";
import css from "./BookingDatePicker.module.css";

/**
 * Compact calendar popover for the booking modal's date chip. Per эталон
 * (screen-03 Frame 1 → date chip click): small floating panel with month
 * nav, weekday header, 6×7 day grid. Selected day fills cyan; weekends are
 * pink-tinted; out-of-month days are dimmed.
 *
 * This is NOT M.1 DateSelector — that one renders a full trigger + popover.
 * Here we only need the popover bit anchored to an external trigger.
 */
export interface BookingDatePickerProps {
  /** Currently selected date (the booking date or override). */
  value: Date;
  /** Fired when user picks a different day. Pops closed after. */
  onChange: (next: Date) => void;
  onClose: () => void;
}

export function BookingDatePicker({ value, onChange, onClose }: BookingDatePickerProps) {
  const [viewMonth, setViewMonth] = useState<Date>(
    () => new Date(value.getFullYear(), value.getMonth(), 1),
  );
  const cells = useMemo(() => getMonthMatrix(viewMonth), [viewMonth]);
  const today = startOfDay(new Date());
  const rootRef = useRef<HTMLDivElement>(null);

  /* Outside click closes the popover. */
  useEffect(() => {
    const h = (e: PointerEvent) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [onClose]);

  /* Esc closes. */
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div ref={rootRef} className={css.popover} role="dialog" aria-label="Выбор даты">
      <div className={css.body}>
        <div className={css.monthNav}>
          <button
            type="button"
            className={css.chev}
            onClick={() => setViewMonth(addMonths(viewMonth, -1))}
            aria-label="Предыдущий месяц"
          >
            ‹
          </button>
          <span className={css.monthLabel}>{fmtMonthHeader(viewMonth)}</span>
          <button
            type="button"
            className={css.chev}
            onClick={() => setViewMonth(addMonths(viewMonth, 1))}
            aria-label="Следующий месяц"
          >
            ›
          </button>
        </div>

        <div className={css.weekdayRow} aria-hidden="true">
          {WEEKDAYS_ISO.map((w, i) => (
            <span
              key={w}
              className={`${css.weekdayCell} ${i >= 5 ? css.weekend : ""}`}
            >
              {w}
            </span>
          ))}
        </div>

        <div className={css.grid} role="grid">
          {cells.map(({ date: d, inMonth }) => {
            const selected = sameDay(d, value);
            const isToday = sameDay(d, today);
            const we = isWeekend(d);
            return (
              <button
                key={fmtIsoDate(d) + (inMonth ? "" : "x")}
                type="button"
                role="gridcell"
                className={[
                  css.cell,
                  !inMonth && css.cellMuted,
                  we && !selected && css.cellWeekend,
                  isToday && !selected && css.cellToday,
                  selected && css.cellSelected,
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  onChange(startOfDay(d));
                  onClose();
                }}
                aria-selected={selected}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
